import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImg from '../../Images/logo_img_small.png';
import { usesUnifiedDates, getDatesForPDF, getDateLabelForPDF } from '../../utils/unifiedDateStorage';
import { getLogoBase64 } from '../../utils/logoBase64';
import { getRentalTermsURL } from '../../config/documents';

// Company branding
const BRAND_COLOR = '#a20000';
const BRAND_COLOR_RGB = [162, 0, 0];

// Company information
const COMPANY_INFO = {
  name: 'Beema Scaffolding (Singapore) Pte Ltd',
  address: '102F Pasir Panjang Road, #04-12',
  address2: 'Citilink Warehouse Complex',
  address3: 'Singapore 118530',
  phone: '+65 85123494',
  website: 'beemascaffolding.sg',
  email: 'contact@beemascaffolding.sg',
  uen: '202340913H'
};

const CONTACT_INFO = {
  name: 'Lina',
  phone: '+65 85123494',
  email: 'contact@beemascaffold.sg'
};

/**
 * Load and convert image to base64 with optimization
 * @param {string} imagePath - Path to image
 * @returns {Promise<string>} Base64 image data
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Optimize canvas size for PDF (smaller dimensions)
      const maxWidth = 150;  // Reduced from original size
      const maxHeight = 100; // Reduced from original size
      
      let { width, height } = img;
      
      // Scale down if image is too large
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Fill canvas with white background to prevent black background in JPEG
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      
      // Use better image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Use JPEG format with 70% quality for much smaller file size
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

/**
 * Helper function to calculate quotation validity date (1 week from today)
 * @returns {string} Formatted date
 */
const getQuotationValidityDate = () => {
  const today = new Date();
  const validityDate = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
  return validityDate.toLocaleDateString('en-GB');
};

/**
 * Generate and download quotation PDF
 * @param {Object} quotationData - Quotation data
 */
export const generateQuotationPDF = async (quotationData) => {
  try {
    console.log('Starting enhanced PDF generation...');
    
    // Validate required data
    if (!quotationData) {
      throw new Error('Quotation data is required');
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const footerHeight = 15; // Reserve space for footer
    const safeAreaBottom = pageHeight - footerHeight;
    let currentY = margin;

    // Page management functions
    const checkPageBreak = (requiredSpace = 10) => {
      if (currentY + requiredSpace > safeAreaBottom) {
        addFooter();
        pdf.addPage();
        currentY = margin;
        return true;
      }
      return false;
    };

    const addFooter = (pageNum = null, totalPagesCount = null) => {
      const footerY = pageHeight - 10;
      const currentPage = pageNum || pdf.internal.getCurrentPageInfo().pageNumber;
      const totalPages = totalPagesCount || pdf.internal.getNumberOfPages();
      
      // Footer separator line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      // Footer content
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('If you have any questions about this quotation, please contact:', margin, footerY);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${CONTACT_INFO.name}, ${CONTACT_INFO.phone}, ${CONTACT_INFO.email}`, margin, footerY + 6);
      
      // Page numbering (right aligned)
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, footerY + 6, { align: 'right' });
    };

    // Function to add footers to all pages after content is complete
    const addFootersToAllPages = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Add acceptance section on the last page, just above the footer
        if (i === totalPages) {
          let acceptanceY = pageHeight - footerHeight - 22; // Position just above footer
          
          // Reset to black for the fields
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          // Accepted By and Date fields side by side on the same line
          const acceptedByLabel = 'Accepted By (Name & Signature):';
          const acceptedByLabelWidth = pdf.getTextWidth(acceptedByLabel);
          const dateLabel = 'Date:';
          const dateLabelWidth = pdf.getTextWidth(dateLabel);
          
          // Calculate positions for two-column layout
          const leftSideStart = margin;
          const leftSideEnd = pageWidth / 2 + 20; // Signature takes more space
          const rightSideStart = leftSideEnd + 10;
          const fieldsY = acceptanceY;
          
          // Left side - Accepted By with signature line
          pdf.text(acceptedByLabel, leftSideStart, fieldsY);
          const signatureLineStart = leftSideStart + acceptedByLabelWidth + 5;
          const signatureLineEnd = leftSideEnd;
          pdf.setDrawColor(100, 100, 100);
          pdf.setLineWidth(0.3);
          pdf.line(signatureLineStart, fieldsY + 1, signatureLineEnd, fieldsY + 1);
          
          // Right side - Date with date line
          pdf.text(dateLabel, rightSideStart, fieldsY);
          const dateLineStart = rightSideStart + dateLabelWidth + 5;
          const dateLineEnd = pageWidth - margin;
          pdf.line(dateLineStart, fieldsY + 1, dateLineEnd, fieldsY + 1);
          
          // Add rental terms acknowledgment text AFTER acceptance fields
          acceptanceY = fieldsY + 10; // Position below the signature line
          
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          
          // Build the text with clickable link inline
          try {
            const rentalTermsURL = getRentalTermsURL();
            const beforeText = 'By accepting this quotation, you acknowledge that you have read and agree to Rental Terms and Conditions. Link: ';
            const linkText = 'beemascaffolding.sg/tnc';
            
            // Calculate text positions
            const beforeWidth = pdf.getTextWidth(beforeText);
            
            // First part of text
            pdf.text(beforeText, margin, acceptanceY);
            
            // Clickable link (blue and underlined) - only the URL
            pdf.setTextColor(0, 0, 255);
            pdf.setFont('helvetica', 'underline');
            pdf.textWithLink(linkText, margin + beforeWidth, acceptanceY, {
              url: rentalTermsURL
            });
            
          } catch (error) {
            console.error('Error adding rental terms link:', error);
            // Fallback without link
            pdf.text('By accepting this quotation, you acknowledge that you have read and agree to Rental Terms and Conditions. Link: beemascaffolding.sg/tnc', margin, acceptanceY);
          }
        }
        
        addFooter(i, totalPages);
      }
    };

    // Helper function to add text with better positioning
    const addText = (text, x, y, options = {}) => {
      const {
        fontSize = 10,
        fontStyle = 'normal',
        align = 'left',
        maxWidth = pageWidth - 2 * margin,
        color = [0, 0, 0]
      } = options;

      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', fontStyle);
      pdf.setTextColor(color[0], color[1], color[2]);

      if (align === 'center') {
        pdf.text(text || '', pageWidth / 2, y, { align: 'center' });
      } else if (align === 'right') {
        pdf.text(text || '', x, y, { align: 'right' });
      } else {
        pdf.text(text || '', x, y);
      }
      
      return y + (fontSize * 0.35) + 2;
    };

    // Load and add logo with preserved aspect ratio using new utility
    let logoLoaded = false;
    
    try {
      console.log('Loading logo using utility function...');
      
      // Use the new logo utility function
      const logoBase64 = await getLogoBase64();
      
      // Create a temporary image to get original dimensions
      const tempImg = new Image();
      tempImg.src = logoBase64;
      await new Promise((resolve, reject) => {
        tempImg.onload = resolve;
        tempImg.onerror = reject;
        // Add timeout to prevent hanging
        setTimeout(reject, 3000);
      });
      
      // Calculate dimensions to preserve aspect ratio
      const maxWidth = 30;
      const maxHeight = 20;
      const aspectRatio = tempImg.width / tempImg.height;
      
      let logoWidth, logoHeight;
      if (aspectRatio > maxWidth / maxHeight) {
        // Image is wider, constrain by width
        logoWidth = maxWidth;
        logoHeight = maxWidth / aspectRatio;
      } else {
        // Image is taller, constrain by height
        logoHeight = maxHeight;
        logoWidth = maxHeight * aspectRatio;
      }
      
      pdf.addImage(logoBase64, 'JPEG', margin, currentY, logoWidth, logoHeight);
      console.log('Logo loaded successfully using utility function');
      logoLoaded = true;
    } catch (error) {
      console.error('Could not load logo using utility function:', error);
      
      // Final fallback: try the original method
      try {
        console.log('Trying final fallback logo loading...');
        const logoBase64 = await loadImageAsBase64(logoImg);
        
        const tempImg = new Image();
        tempImg.src = logoBase64;
        await new Promise((resolve, reject) => {
          tempImg.onload = resolve;
          tempImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const maxWidth = 30;
        const maxHeight = 20;
        const aspectRatio = tempImg.width / tempImg.height;
        
        let logoWidth, logoHeight;
        if (aspectRatio > maxWidth / maxHeight) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / aspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * aspectRatio;
        }
        
        pdf.addImage(logoBase64, 'JPEG', margin, currentY, logoWidth, logoHeight);
        console.log('Logo loaded successfully from final fallback method');
        logoLoaded = true;
      } catch (fallbackError) {
        console.error('Final fallback logo loading also failed:', fallbackError);
      }
    }

    if (!logoLoaded) {
      console.error('Failed to load logo. PDF will continue without logo.');
    }

    // HEADER SECTION - Company name next to logo, QUOTATION on right closer to date
    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY_INFO.name, margin + 32, currentY + 8);
    pdf.setFontSize(11);
    pdf.text('QUOTATION', pageWidth - margin, currentY + 18, { align: 'right' });
    
    currentY += 25;

    // Company address block (smaller font)
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    currentY = addText(COMPANY_INFO.address, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address2, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address3, margin, currentY, { fontSize: 8 });
    currentY += 1;
    currentY = addText(`Phone: ${COMPANY_INFO.phone}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Website: ${COMPANY_INFO.website}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Email: ${COMPANY_INFO.email}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`UEN: ${COMPANY_INFO.uen}`, margin, currentY, { fontSize: 8 });

    // Date and quotation number (properly right aligned with consistent spacing)
    // Use creation date if available, otherwise last modified date, otherwise current date
    const getQuotationDate = () => {
      if (quotationData.createdAt) {
        // If createdAt is a Date object, format it
        if (quotationData.createdAt instanceof Date) {
          return quotationData.createdAt.toLocaleDateString('en-GB');
        }
        // If createdAt is a string, use it directly
        if (typeof quotationData.createdAt === 'string') {
          return quotationData.createdAt;
        }
      }
      
      if (quotationData.updatedAt) {
        // If updatedAt is a Date object, format it
        if (quotationData.updatedAt instanceof Date) {
          return quotationData.updatedAt.toLocaleDateString('en-GB');
        }
        // If updatedAt is a string, use it directly
        if (typeof quotationData.updatedAt === 'string') {
          return quotationData.updatedAt;
        }
      }
      
      // Fallback to provided date or current date
      return quotationData.date || new Date().toLocaleDateString('en-GB');
    };

    const dateInfoY = margin + 25;
    addText(`DATE: ${getQuotationDate()}`, pageWidth - margin, dateInfoY, { 
      fontSize: 10, 
      align: 'right' 
    });
    addText(`QUOTATION #: ${quotationData.quotationNumber || 'DRAFT'}`, pageWidth - margin, dateInfoY + 7, { 
      fontSize: 10, 
      align: 'right' 
    });



    // Add horizontal line separator before client and quotation details
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;

    // TWO-COLUMN LAYOUT FOR CUSTOMER AND JOB DETAILS
    const leftColX = margin;
    const rightColX = pageWidth / 2 + 10;
    const sectionStartY = currentY;

    // LEFT COLUMN - Customer Details
    
    let leftY = sectionStartY + 2;
    
    // Set consistent font settings for all customer information
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    // Company/Customer name
    const customerName = quotationData.companyName || quotationData.customerName || 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('To:', leftColX, leftY);
    const toLabelWidth = pdf.getTextWidth('To: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(customerName, leftColX + toLabelWidth, leftY);
    leftY += 6;
    
    // Attention - customer name if company exists, otherwise N/A
    const attnText = quotationData.companyName ? (quotationData.attn || 'N/A') : 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('Attn:', leftColX, leftY);
    const attnLabelWidth = pdf.getTextWidth('Attn: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(attnText, leftColX + attnLabelWidth, leftY);
    leftY += 6;
    
    // Phone - enhanced fix for character spacing issue
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    
    // Render "Phone:" label first in bold
    pdf.text('Phone:', leftColX, leftY);
    
    // Calculate width of "Phone: " to position the value correctly
    const phoneLabel = 'Phone: ';
    const phoneLabelWidth = pdf.getTextWidth(phoneLabel);
    
    // Switch to normal font for the value
    pdf.setFont('helvetica', 'normal');
    
    // Enhanced phone value cleaning and rendering
    const phoneValue = quotationData.tel || 'N/A';
    
    // Aggressive data sanitization
    let cleanPhoneValue = phoneValue.toString()
      .trim()
      .normalize('NFD') // Unicode normalization
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/[^\x20-\x7E]/g, '') // Keep only printable ASCII characters
      .trim();
    
    // If still having issues, try character-by-character rendering
    try {
      // First try normal rendering
      pdf.text(cleanPhoneValue, leftColX + phoneLabelWidth, leftY);
    } catch (error) {
      console.warn('Normal phone rendering failed, trying character-by-character:', error);
      // Fallback: render character by character
      let currentX = leftColX + phoneLabelWidth;
      for (let i = 0; i < cleanPhoneValue.length; i++) {
        const char = cleanPhoneValue[i];
        pdf.text(char, currentX, leftY);
        currentX += pdf.getTextWidth(char);
      }
    }
    leftY += 6;
    
    // Email - ensure consistent font settings
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Email:', leftColX, leftY);
    const emailLabelWidth = pdf.getTextWidth('Email: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(quotationData.email || 'N/A', leftColX + emailLabelWidth, leftY);
    leftY += 6;
    
    // Billing Address (only if it has a value)
    if (quotationData.billingAddress && quotationData.billingAddress.trim() !== '') {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Billing Address:', leftColX, leftY);
      const billingAddressLabelWidth = pdf.getTextWidth('Billing Address: ');
      pdf.setFont('helvetica', 'normal');
      
      // Split the billing address text and handle multi-line display
      const billingAddressText = quotationData.billingAddress;
      const availableWidth = (pageWidth / 2) - 20 - billingAddressLabelWidth;
      const billingAddressLines = pdf.splitTextToSize(billingAddressText, availableWidth);
      
      // First line goes next to the label
      if (billingAddressLines.length > 0) {
        pdf.text(billingAddressLines[0], leftColX + billingAddressLabelWidth, leftY);
        leftY += 6;
        
        // Additional lines go on new lines with proper indentation
        for (let i = 1; i < billingAddressLines.length; i++) {
          pdf.text(billingAddressLines[i], leftColX + billingAddressLabelWidth, leftY);
          leftY += 6;
        }
      }
    }
    

    // RIGHT COLUMN - Job Details
    let rightY = sectionStartY + 2;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    // Quotation Type
    pdf.setFont('helvetica', 'bold');
    pdf.text('Quotation Type:', rightColX, rightY);
    const quotationTypeLabelWidth = pdf.getTextWidth('Quotation Type: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(getQuotationTypeLabel(quotationData.quotationType), rightColX + quotationTypeLabelWidth, rightY);
    rightY += 6;
    
    // Site Address (only if it has a value)
    if (quotationData.siteAddress && quotationData.siteAddress.trim() !== '') {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Site Address:', rightColX, rightY);
      const siteAddressLabelWidth = pdf.getTextWidth('Site Address: ');
      pdf.setFont('helvetica', 'normal');
      
      // Split the address text and handle multi-line display
      const siteAddressText = quotationData.siteAddress;
      const availableWidth = (pageWidth / 2) - 20 - siteAddressLabelWidth;
      const siteAddressLines = pdf.splitTextToSize(siteAddressText, availableWidth);
      
      // First line goes next to the label
      if (siteAddressLines.length > 0) {
        pdf.text(siteAddressLines[0], rightColX + siteAddressLabelWidth, rightY);
        rightY += 6;
        
        // Additional lines go on new lines with proper indentation
        for (let i = 1; i < siteAddressLines.length; i++) {
          pdf.text(siteAddressLines[i], rightColX + siteAddressLabelWidth, rightY);
          rightY += 6;
        }
      }
    }
    
    // Display date information using unified date system
    const datesDisplay = getDatesForPDF(quotationData);
    if (datesDisplay) {
      const dateLabel = getDateLabelForPDF(quotationData);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${dateLabel}:`, rightColX, rightY);
      const datesLabelWidth = pdf.getTextWidth(`${dateLabel}: `);
      pdf.setFont('helvetica', 'normal');

      // Handle multi-line dates display
      const availableWidth = (pageWidth / 2) - 20 - datesLabelWidth;
      const datesLines = pdf.splitTextToSize(datesDisplay, availableWidth);

      // First line goes next to the label
      if (datesLines.length > 0) {
        pdf.text(datesLines[0], rightColX + datesLabelWidth, rightY);
        rightY += 6;

        // Additional lines go on new lines with proper indentation
        for (let i = 1; i < datesLines.length; i++) {
          pdf.text(datesLines[i], rightColX + datesLabelWidth, rightY);
          rightY += 6;
        }
      }
    }
    
    // Quotation Validity
    const validityDate = quotationData.quotationValidity || getQuotationValidityDate();
    pdf.setFont('helvetica', 'bold');
    pdf.text('Quotation Validity:', rightColX, rightY);
    const quotationValidityLabelWidth = pdf.getTextWidth('Quotation Validity: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${validityDate} (1 week)`, rightColX + quotationValidityLabelWidth, rightY);
    rightY += 6;
    
    // Payment Terms
    pdf.setFont('helvetica', 'bold');
    pdf.text('Payment Terms:', rightColX, rightY);
    const paymentTermsLabelWidth = pdf.getTextWidth('Payment Terms: ');
    pdf.setFont('helvetica', 'normal');
    
    // Use custom payment terms if available, otherwise use stored payment terms
    const paymentTermsValue = quotationData.customPaymentTerms || quotationData.paymentTerms || 'TBC';
    
    // Handle multi-line payment terms
    const availableWidth = (pageWidth / 2) - 20 - paymentTermsLabelWidth;
    const paymentTermsLines = pdf.splitTextToSize(paymentTermsValue, availableWidth);
    
    // First line goes next to the label
    if (paymentTermsLines.length > 0) {
      pdf.text(paymentTermsLines[0], rightColX + paymentTermsLabelWidth, rightY);
      rightY += 6;
      
      // Additional lines go on new lines with proper indentation
      for (let i = 1; i < paymentTermsLines.length; i++) {
        pdf.text(paymentTermsLines[i], rightColX + paymentTermsLabelWidth, rightY);
        rightY += 6;
      }
    }

    currentY = Math.max(leftY, rightY) + 3;

    // SERVICES/PRODUCTS TABLE using autoTable for proper multi-page handling
    if (quotationData.lineItems && Array.isArray(quotationData.lineItems) && quotationData.lineItems.length > 0) {
      const lineItems = quotationData.lineItems.filter(item => item && item.description && item.description.trim() !== '');
      
      if (lineItems.length > 0) {
        // Prepare table data
        const tableData = lineItems.map((item, index) => [
          (index + 1).toString(),
          item.description || '',
          (item.quantity || 0).toString(),
          `$${(item.unitPrice || 0).toFixed(2)}`,
          `$${(item.total || 0).toFixed(2)}`
        ]);

        // Configure autoTable
        autoTable(pdf, {
          startY: currentY,
          head: [['#', 'Description', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: BRAND_COLOR_RGB,
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
            halign: 'left'
          },
          bodyStyles: {
            fontSize: 9,
            textColor: [0, 0, 0],
            halign: 'left'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' }, // #
            1: { cellWidth: 111, halign: 'left' },  // Description
            2: { cellWidth: 20, halign: 'center' }, // Qty
            3: { cellWidth: 20, halign: 'right' },  // Unit Price
            4: { cellWidth: 20, halign: 'right' }   // Total
          },
          margin: { left: margin, right: margin },
          tableWidth: 186,
          styles: {
            cellPadding: 2,
            lineColor: [0, 0, 0],
            lineWidth: 0.1
          },
          // Handle page breaks properly
          didDrawPage: function(data) {
            // This ensures proper spacing from footer
            const pageHeight = pdf.internal.pageSize.getHeight();
            if (data.cursor.y > pageHeight - footerHeight - 10) {
              // Move to next page if too close to footer area
              return false;
            }
          },
          // Reserve space for footer
          pageBreak: 'auto',
          showFoot: 'everyPage',
          margin: { 
            left: margin, 
            right: margin, 
            top: margin, 
            bottom: footerHeight + 5 
          }
        });

        // Update currentY to position after the table
        currentY = pdf.lastAutoTable.finalY + 10;
      }
    }

    // COMBINED PRICING AND ADDITIONAL SERVICES SECTION
    const pricingSectionStartY = currentY;
    let leftSectionY = pricingSectionStartY;
    let rightSectionY = pricingSectionStartY;

    // LEFT SIDE - ADDITIONAL SERVICES (if any exist)
    if (quotationData.additionalServices) {
      const selectedServices = Object.entries(quotationData.additionalServices)
        .filter(([key, service]) => service.selected);
      
      if (selectedServices.length > 0) {
        // Check if we need a page break for additional services section
        const requiredSpace = 20 + (selectedServices.length * 6);
        checkPageBreak(requiredSpace);
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Rate of Additional Services (for reference):', margin, leftSectionY);
        leftSectionY += 8;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        selectedServices.forEach(([key, service]) => {
          const serviceName = getServiceDisplayName(key);
          pdf.text(`• ${serviceName} - $${service.price}`, margin, leftSectionY);
          leftSectionY += 6;
        });
        leftSectionY += 10;
      }
    }

    // RIGHT SIDE - PRICING SECTION (conditionally displayed based on showTotalSegment)
    // Check if pricing should be shown based on showTotalSegment checkbox
    const shouldShowPricing = quotationData.showTotalSegment !== false;
    
    if (shouldShowPricing) {
      // Subtotal
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Subtotal: $${(quotationData.subtotal || 0).toFixed(2)}`, pageWidth - margin, rightSectionY, { align: 'right' });
      rightSectionY += 8;
      
      // Discount (if applicable)
      if (quotationData.discount && quotationData.discountAmount && quotationData.discountAmount > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(`Discount: -$${quotationData.discountAmount.toFixed(2)}`, pageWidth - margin, rightSectionY, { align: 'right' });
        pdf.setTextColor(0, 0, 0); // Reset to black
        rightSectionY += 8;
      }
      
      // GST (if applicable)
      if (quotationData.gst && quotationData.gst.percentage > 0 && quotationData.gstAmount > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(`GST (${quotationData.gst.percentage}%): +$${quotationData.gstAmount.toFixed(2)}`, pageWidth - margin, rightSectionY, { align: 'right' });
        pdf.setTextColor(0, 0, 0); // Reset to black
        rightSectionY += 8;
      }
      
      // Grand Total with line
      pdf.setDrawColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
      pdf.setLineWidth(0.7);
      pdf.line(pageWidth - 60, rightSectionY - 2, pageWidth - margin, rightSectionY - 2);
      
      pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`TOTAL (SGD): $${(quotationData.grandTotal || 0).toFixed(2)}`, pageWidth - margin, rightSectionY + 5, { align: 'right' });
      
      pdf.line(pageWidth - 60, rightSectionY + 10, pageWidth - margin, rightSectionY + 10);
      rightSectionY += 10;
    }

    // Update currentY to the maximum of both sections
    currentY = Math.max(leftSectionY, rightSectionY);

    // SERVICES SECTION (if any services are selected)
    if (quotationData.services) {
      const { transportation, assemblyDismantle, endorsement, siteAddress } = quotationData.services;
      const hasSelectedServices = transportation || assemblyDismantle || endorsement;
      
      if (hasSelectedServices) {
        // Check if we need a page break for services section
        checkPageBreak(25);
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Services:', margin, currentY);
        currentY += 6;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        
        if (transportation) {
          pdf.text('• Delivery and Collection (Transportation)', margin, currentY);
          currentY += 5;
        }
        
        if (assemblyDismantle) {
          pdf.text('• Assemble and Dismantle', margin, currentY);
          currentY += 5;
        }
        
        if (endorsement) {
          pdf.text('• Endorsement (Weekly)', margin, currentY);
          currentY += 5;
        }
        
        if (siteAddress && siteAddress.trim() !== '') {
          pdf.text(`Site Address: ${siteAddress}`, margin, currentY);
          
        }
        
        currentY += 8;
      }
    }

    // CUSTOM ADDITIONAL SERVICES
    if (quotationData.customAdditionalServices && quotationData.customAdditionalServices.trim() !== '') {
      // Check if we need a page break for custom additional services section
      checkPageBreak(20);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Other Additional Services:', margin, currentY);
      currentY += 6;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const customServicesLines = pdf.splitTextToSize(quotationData.customAdditionalServices, pageWidth - 2 * margin);
      customServicesLines.forEach(line => {
        checkPageBreak(8); // Check before each line
        pdf.text(line, margin, currentY);
        currentY += 5;
      });
      currentY += 8;
    }

    // ADDITIONAL NOTES
    if (quotationData.notes && quotationData.notes.trim() !== '') {
      // Check if we need a page break for notes section
      checkPageBreak(20);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Additional Notes:', margin, currentY);
      currentY += 6;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const noteLines = pdf.splitTextToSize(quotationData.notes, pageWidth - 2 * margin);
      noteLines.forEach(line => {
        checkPageBreak(8); // Check before each line
        pdf.text(line, margin, currentY);
        currentY += 5;
      });
      currentY += 8;
    }

    // Add footers to all pages with correct page numbering
    // The acceptance section will be added to the last page just above the footer
    addFootersToAllPages();

    // Generate filename
    const companyName = (quotationData.companyName || quotationData.customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const quotationNumber = quotationData.quotationNumber || 'DRAFT';
    const fileName = `${quotationNumber}.pdf`;
    
    console.log('Enhanced PDF generated successfully');
    
    // Save the PDF
    pdf.save(fileName);
    
    return true;
    
  } catch (error) {
    console.error('Enhanced PDF generation error:', error);
    throw new Error(`PDF generation failed: ${error.message}`);
  }
};

/**
 * Get display label for quotation type
 * @param {string} type - Quotation type
 * @returns {string} Display label
 */
const getQuotationTypeLabel = (type) => {
  const labels = {
    'purchase': 'Purchase',
    'rental': 'Rental',
    'rental_extension': 'Rental Extension',
    'scaffolding_service': 'Scaffolding Service'
  };
  return labels[type] || type;
};

/**
 * Get display name for additional service
 * @param {string} serviceKey - Service key
 * @returns {string} Display name
 */
const getServiceDisplayName = (serviceKey) => {
  const names = {
    'additionalTransport': 'Additional transport of scaffold',
    'additionalInstallation': 'Additional installation / dismantling',
    'stairsInstallation': 'Installation of scaffold on stairs',
    'stairsLoading': 'Loading & unloading of scaffold via stairs',
    'weeklyInspection': 'Weekly Scaffold inspection and endorsement'
  };
  return names[serviceKey] || serviceKey;
};

export default generateQuotationPDF;
